import React, { useEffect, useMemo, useState } from 'react'
import { apiErrorMessage } from '../../../services/access'
import { mentorshipService } from '../../../services/mentorship'
import type { MentorshipSkill } from '../../../services/mentorship'
import { SkillPicker } from '../../ui/SkillPicker'
import type { SkillCatalogResult } from '../../../services/skills'
import { useAccessContext } from '../../../context/AccessContext'
import { useI18n } from '../../../i18n/I18nContext'
import { EmptyState, ErrorBanner, LoadingState } from '../admin/AdminUiPrimitives'

type TabId = 'overview' | 'mentor' | 'apprentice' | 'bookings' | 'sessions' | 'payments'
type AnyRow = Record<string, any>

const tabs: Array<{ id: TabId; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'mentor', label: 'Mentor Mode' },
  { id: 'apprentice', label: 'Apprentice Mode' },
  { id: 'bookings', label: 'Bookings' },
  { id: 'sessions', label: 'Sessions' },
  { id: 'payments', label: 'Payments' },
]

const statusClass = (status?: string | null) => {
  const value = String(status || '').toLowerCase()
  if (['approved', 'accepted', 'active', 'completed', 'recorded', 'paid_off_platform'].includes(value)) return 'status-pill status-pill--approved'
  if (['pending', 'requested', 'scheduled', 'proposed'].includes(value)) return 'status-pill status-pill--pending'
  if (['rejected', 'cancelled', 'disputed', 'suspended', 'no_show'].includes(value)) return 'status-pill status-pill--rejected'
  return 'status-pill status-pill--muted'
}

const titleCase = (value?: string | null) => String(value || '-').replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
const rowId = (row: AnyRow) => String(row.id || row.skillId || row.skill_id || Math.random())
const skillIdOf = (row: AnyRow) => String(row.skillId || row.skill_id || '')
const skillNameOf = (row: AnyRow) => String(row.skillName || row.skill_name || row.name || 'Skill')
const skillOptionIdOf = (skill: MentorshipSkill) => String(skill.skillId || skill.id)
const skillLabelOf = (skill: MentorshipSkill) => `${skill.name}${skill.category ? ` (${skill.category})` : ''}`
const workerIdOf = (row: AnyRow, key: string) => String(row[key] || row[key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)] || '')
const amountOf = (row: AnyRow, key: string) => row[key] ?? row[key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)]
const responseRows = (response: AnyRow | null, key: string) => Array.isArray(response?.data) ? response.data : (response?.data?.[key] || [])
const sourceLabel = (source?: string) => {
  if (source === 'worker_profile') return 'Already on your profile'
  if (source === 'mentorship_catalog') return 'Mentorship catalog'
  return 'Shared catalog'
}

export const WorkerMentorshipPanel: React.FC = () => {
  const { t, formatDateTime, formatCurrency, formatNumber } = useI18n()
  const { currentBrandName, currentLogoUrl, brandStyleVars } = useAccessContext()
  const [activeTab, setActiveTab] = useState<TabId>('overview')
  const [profile, setProfile] = useState<AnyRow | null>(null)
  const [skills, setSkills] = useState<MentorshipSkill[]>([])
  const [claims, setClaims] = useState<AnyRow[]>([])
  const [goals, setGoals] = useState<AnyRow[]>([])
  const [matches, setMatches] = useState<AnyRow[]>([])
  const [sessions, setSessions] = useState<AnyRow[]>([])
  const [rates, setRates] = useState<AnyRow[]>([])
  const [bookings, setBookings] = useState<AnyRow[]>([])
  const [payments, setPayments] = useState<AnyRow[]>([])
  const [suggestedMentors, setSuggestedMentors] = useState<AnyRow[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [preferences, setPreferences] = useState({ mentorModeEnabled: false, apprenticeModeEnabled: false, remoteMentorshipEnabled: true, onsiteMentorshipEnabled: true, bio: '' })
  const [claimForm, setClaimForm] = useState({ skillId: '', notes: '' })
  const [claimSkills, setClaimSkills] = useState<SkillCatalogResult[]>([])
  const [rateForm, setRateForm] = useState({ skillId: '', rateAmount: '', currencyCode: 'CAD', rateUnit: 'session' })
  const [goalForm, setGoalForm] = useState({ skillId: '', priority: 'medium', notes: '' })
  const [goalSkillsSelected, setGoalSkillsSelected] = useState<SkillCatalogResult[]>([])
  const [bookingForm, setBookingForm] = useState({ mentorWorkerProfileId: '', skillId: '', mentorRateId: '', requestedStartAt: '', requestedEndAt: '', apprenticeNotes: '' })
  const [reviewForm, setReviewForm] = useState({ matchId: '', revieweeWorkerProfileId: '', rating: '5', reviewType: 'apprentice_review', notes: '' })

  const myWorkerProfileId = String(profile?.workerProfileId || profile?.worker_profile_id || '')
  const approvedClaims = claims.filter((claim) => claim.status === 'approved')
  const pendingClaims = claims.filter((claim) => claim.status === 'pending')
  const approvedSkillIds = new Set(approvedClaims.map(skillIdOf))
  const claimableSkills = skills.filter((skill) => skill.canCreateMentorClaim !== false && skill.status !== 'inactive' && skill.status !== 'archived')
  const goalSkills = skills.filter((skill) => skill.canCreateLearningGoal !== false && skill.status !== 'inactive' && skill.status !== 'archived')
  const activeSkills = skills.filter((skill) => skill.status !== 'inactive' && skill.status !== 'archived')
  const activeGoals = goals.filter((goal) => ['active', 'open', 'pending'].includes(String(goal.status || 'active')))
  const activeBookings = bookings.filter((booking) => ['requested', 'accepted'].includes(String(booking.status)))
  const upcomingSessions = sessions.filter((session) => ['scheduled', 'accepted'].includes(String(session.status || 'scheduled')))
  const completedSessions = sessions.filter((session) => String(session.status) === 'completed')
  const incomingBookings = bookings.filter((booking) => workerIdOf(booking, 'mentorWorkerProfileId') === myWorkerProfileId)
  const outgoingBookings = bookings.filter((booking) => workerIdOf(booking, 'apprenticeWorkerProfileId') === myWorkerProfileId)
  const activeRatesBySkill = useMemo(() => new Map(rates.filter((rate) => rate.isActive !== false).map((rate) => [skillIdOf(rate), rate])), [rates])
  const mentorOptions = suggestedMentors.filter((mentor) => activeRatesBySkill.has(skillIdOf(mentor)) || mentor.rateAmount || mentor.rate_amount)

  const refresh = async () => {
    const results = await Promise.allSettled([
      mentorshipService.getMyMentorshipProfile(),
      mentorshipService.listMentorshipSkills(),
      mentorshipService.listMyMentorClaims(),
      mentorshipService.listMyLearningGoals(),
      mentorshipService.listMyMatches(),
      mentorshipService.listMySessions(),
      mentorshipService.listMyRates(),
      mentorshipService.listMyBookings(),
      mentorshipService.listMyPaymentRecords(),
    ])
    const [profileResponse, skillsResponse, claimsResponse, goalsResponse, matchesResponse, sessionsResponse, ratesResponse, bookingsResponse, paymentsResponse] = results.map((result) => result.status === 'fulfilled' ? result.value : null)
    const failed = results.find((result) => result.status === 'rejected')
    if (failed?.status === 'rejected') {
      setError(apiErrorMessage(failed.reason, t('Unable to load one or more mentorship sections.')))
    } else {
      setError('')
    }
    const nextProfile = profileResponse?.data?.profile || {}
    const nextPreferences = nextProfile.preferences || nextProfile
    setProfile(nextProfile)
    setPreferences({
      mentorModeEnabled: Boolean(nextPreferences.mentorModeEnabled),
      apprenticeModeEnabled: Boolean(nextPreferences.apprenticeModeEnabled),
      remoteMentorshipEnabled: nextPreferences.remoteMentorshipEnabled !== false,
      onsiteMentorshipEnabled: nextPreferences.onsiteMentorshipEnabled !== false,
      bio: nextPreferences.bio || '',
    })
    setSkills(responseRows(skillsResponse, 'skills'))
    setClaims(responseRows(claimsResponse, 'claims'))
    setGoals(responseRows(goalsResponse, 'learningGoals'))
    setMatches(responseRows(matchesResponse, 'matches'))
    setSessions(responseRows(sessionsResponse, 'sessions'))
    setRates(responseRows(ratesResponse, 'rates'))
    setBookings(responseRows(bookingsResponse, 'bookings'))
    setPayments(responseRows(paymentsResponse, 'paymentRecords'))
  }

  useEffect(() => {
    let mounted = true
    setLoading(true)
    refresh()
      .catch((err) => mounted && setError(apiErrorMessage(err, t('Unable to load mentorship workspace.'))))
      .finally(() => mounted && setLoading(false))
    return () => { mounted = false }
  }, [])

  const run = async (action: () => Promise<void>, success: string) => {
    setSaving(true)
    setError('')
    setMessage('')
    try {
      await action()
      await refresh()
      setMessage(t(success))
    } catch (err) {
      setError(apiErrorMessage(err, t('Action failed.')))
    } finally {
      setSaving(false)
    }
  }

  const loadSuggestedMentors = async (skillId: string) => {
    setBookingForm((current) => ({ ...current, skillId, mentorWorkerProfileId: '', mentorRateId: '' }))
    setSuggestedMentors([])
    if (!skillId) return
    try {
      const response = await mentorshipService.listSuggestedMentors(skillId, true)
      setSuggestedMentors(response.data?.mentors || [])
    } catch (err) {
      setError(apiErrorMessage(err, t('Unable to load suggested mentors.')))
    }
  }

  const savePreferences = (event: React.FormEvent) => {
    event.preventDefault()
    run(async () => { await mentorshipService.updateMyMentorshipPreferences(preferences) }, 'Mentorship preferences saved.')
  }

  const submitClaim = (event: React.FormEvent) => {
    event.preventDefault()
    const skillId = claimSkills[0]?.id || claimForm.skillId
    if (!skillId) return setError(t('Select a skill before submitting a claim.'))
    run(async () => {
      await mentorshipService.createMentorClaim({ skillId, notes: claimForm.notes || null })
      setClaimForm({ skillId: '', notes: '' })
      setClaimSkills([])
    }, 'Mentor skill claim submitted.')
  }

  const saveRate = (event: React.FormEvent) => {
    event.preventDefault()
    const rateAmount = Number(rateForm.rateAmount)
    if (!rateForm.skillId) return setError(t('Select an approved skill before saving a rate.'))
    if (!approvedSkillIds.has(rateForm.skillId)) return setError(t('Rates can only be set for approved mentor skills.'))
    if (!Number.isFinite(rateAmount) || rateAmount <= 0) return setError(t('Rate must be greater than zero.'))
    const existing = rates.find((rate) => skillIdOf(rate) === rateForm.skillId && rate.isActive !== false)
    run(async () => {
      const payload = { skillId: rateForm.skillId, rateAmount, currencyCode: rateForm.currencyCode, rateUnit: rateForm.rateUnit, isActive: true }
      if (existing?.id) await mentorshipService.updateMyRate(existing.id, payload)
      else await mentorshipService.createMyRate(payload)
      setRateForm({ skillId: '', rateAmount: '', currencyCode: 'CAD', rateUnit: 'session' })
    }, existing ? 'Mentor rate updated.' : 'Mentor rate created.')
  }

  const createGoal = (event: React.FormEvent) => {
    event.preventDefault()
    const skillId = goalSkillsSelected[0]?.id || goalForm.skillId
    if (!skillId) return setError(t('Select a learning skill before creating a goal.'))
    run(async () => {
      await mentorshipService.createLearningGoal({ skillId, priority: goalForm.priority, notes: goalForm.notes || null })
      setGoalForm({ skillId: '', priority: 'medium', notes: '' })
      setGoalSkillsSelected([])
      await loadSuggestedMentors(skillId)
    }, 'Learning goal created.')
  }

  const createBooking = (event: React.FormEvent) => {
    event.preventDefault()
    const start = bookingForm.requestedStartAt ? new Date(bookingForm.requestedStartAt) : null
    const end = bookingForm.requestedEndAt ? new Date(bookingForm.requestedEndAt) : null
    const selectedMentor = suggestedMentors.find((mentor) => workerIdOf(mentor, 'mentorWorkerProfileId') === bookingForm.mentorWorkerProfileId || workerIdOf(mentor, 'workerProfileId') === bookingForm.mentorWorkerProfileId)
    const selectedRate = rates.find((rate) => rate.id === bookingForm.mentorRateId) || selectedMentor
    if (!bookingForm.skillId || !bookingForm.mentorWorkerProfileId) return setError(t('Select a mentor and skill before requesting a booking.'))
    if (!start || !end) return setError(t('Requested booking start and end are required.'))
    if (end.getTime() <= start.getTime()) return setError(t('Requested booking end must be after the start.'))
    if (!bookingForm.mentorRateId && !(selectedRate?.rateAmount || selectedRate?.rate_amount)) return setError(t('Select a mentor rate before booking.'))
    run(async () => {
      await mentorshipService.createBooking({
        mentorWorkerProfileId: bookingForm.mentorWorkerProfileId,
        skillId: bookingForm.skillId,
        mentorRateId: bookingForm.mentorRateId || selectedRate?.id || null,
        requestedStartAt: start.toISOString(),
        requestedEndAt: end.toISOString(),
        apprenticeNotes: bookingForm.apprenticeNotes || null,
      })
      setBookingForm({ mentorWorkerProfileId: '', skillId: '', mentorRateId: '', requestedStartAt: '', requestedEndAt: '', apprenticeNotes: '' })
      setSuggestedMentors([])
    }, 'Booking requested.')
  }

  const bookingAction = (booking: AnyRow, action: 'accept' | 'reject' | 'cancel' | 'complete') => {
    if (['accept', 'reject'].includes(action) && booking.status !== 'requested') return setError(t('Only requested bookings can be accepted or rejected.'))
    if (action === 'cancel' && !['requested', 'pending'].includes(String(booking.status))) return setError(t('Only pending or requested bookings can be cancelled.'))
    if (action === 'complete' && booking.status !== 'accepted') return setError(t('Only accepted bookings can be completed.'))
    run(async () => {
      if (action === 'accept') await mentorshipService.acceptBooking(booking.id)
      if (action === 'reject') await mentorshipService.rejectBooking(booking.id)
      if (action === 'cancel') await mentorshipService.cancelBooking(booking.id)
      if (action === 'complete') await mentorshipService.completeBooking(booking.id)
    }, `Booking ${action}ed.`)
  }

  const submitReview = (event: React.FormEvent) => {
    event.preventDefault()
    const rating = Number(reviewForm.rating)
    if (!reviewForm.matchId || !reviewForm.revieweeWorkerProfileId) return setError(t('Select a match and review target.'))
    if (!Number.isFinite(rating) || rating < 1 || rating > 5) return setError(t('Rating must be from 1 to 5.'))
    run(async () => {
      await mentorshipService.createReview({ ...reviewForm, rating })
      setReviewForm({ matchId: '', revieweeWorkerProfileId: '', rating: '5', reviewType: 'apprentice_review', notes: '' })
    }, 'Review submitted.')
  }

  const renderBookingsTable = (rows: AnyRow[], role: 'mentor' | 'apprentice') => (
    <div className="admin-table-wrap">
      <table className="admin-table">
        <thead><tr><th>{t('Skill')}</th><th>{t('When')}</th><th>{t('Rate')}</th><th>{t('Status')}</th><th>{t('Actions')}</th></tr></thead>
        <tbody>
          {rows.map((booking) => (
            <tr key={booking.id}>
              <td>{skillNameOf(booking)}</td>
              <td>{booking.requestedStartAt ? formatDateTime(booking.requestedStartAt) : '-'}</td>
              <td>{amountOf(booking, 'proposedRateAmount') ? formatCurrency(amountOf(booking, 'proposedRateAmount'), booking.currencyCode || 'CAD') : '-'}</td>
              <td><span className={statusClass(booking.status)}>{titleCase(booking.status)}</span></td>
              <td className="inline-group">
                {role === 'mentor' ? <>
                  <button type="button" className="btn btn-secondary" disabled={saving || booking.status !== 'requested'} onClick={() => bookingAction(booking, 'accept')}>{t('Accept')}</button>
                  <button type="button" className="btn btn-ghost" disabled={saving || booking.status !== 'requested'} onClick={() => bookingAction(booking, 'reject')}>{t('Reject')}</button>
                </> : null}
                {role === 'apprentice' ? <button type="button" className="btn btn-ghost" disabled={saving || !['requested', 'pending'].includes(String(booking.status))} onClick={() => bookingAction(booking, 'cancel')}>{t('Cancel')}</button> : null}
                <button type="button" className="btn btn-ghost" disabled={saving || booking.status !== 'accepted'} onClick={() => bookingAction(booking, 'complete')}>{t('Complete')}</button>
              </td>
            </tr>
          ))}
          {rows.length === 0 ? <tr><td colSpan={5}>{t('No bookings found.')}</td></tr> : null}
        </tbody>
      </table>
    </div>
  )

  const renderSkillBadges = (skill: MentorshipSkill) => (
    <>
      <span className="admin-chip">{t(sourceLabel(skill.source))}</span>
      {skill.workerHasSkill ? <span className="admin-chip">{t('Already on your profile')}</span> : null}
      {skill.mentorClaimStatus && skill.mentorClaimStatus !== 'none' ? <span className={statusClass(skill.mentorClaimStatus)}>{t(skill.mentorClaimStatus === 'approved' ? 'Approved to mentor' : titleCase(skill.mentorClaimStatus))}</span> : <span className="admin-chip">{t('Available to claim')}</span>}
      {skill.learningGoalStatus && skill.learningGoalStatus !== 'none' ? <span className={statusClass(skill.learningGoalStatus)}>{t(`Learning goal ${skill.learningGoalStatus}`)}</span> : null}
      {skill.acquiredThroughMentorship ? <span className={statusClass('completed')}>{t('Acquired through mentorship')}</span> : null}
      {skill.status !== 'active' ? <span className={statusClass(skill.status)}>{titleCase(skill.status)}</span> : null}
    </>
  )

  if (loading) return <LoadingState label={t('Loading mentorship workspace...')} />

  return (
    <section className="panel workspace-panel stack" data-testid="worker-mentorship" style={brandStyleVars}>
      <div className="admin-workspace-header" style={{ borderTop: '3px solid var(--brand-primary, var(--primary))' }}>
        <div className="inline-group" style={{ alignItems: 'center' }}>
          {currentLogoUrl ? <img src={currentLogoUrl} alt="" className="admin-brand-logo" /> : null}
          <div>
            <p className="helper" style={{ margin: 0 }}>{currentBrandName}</p>
            <h2 className="heading-lg">{t('Mentorship')}</h2>
            <p className="text-muted">{t('Manage mentor skills, learning goals, bookings, sessions, and internal payment records.')}</p>
          </div>
        </div>
      </div>
      {error ? <ErrorBanner message={error} /> : null}
      {message ? <p className="success-text">{message}</p> : null}
      <div className="admin-tabs" role="tablist" aria-label={t('Worker mentorship sections')}>
        {tabs.map((tab) => <button key={tab.id} type="button" className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>{t(tab.label)}</button>)}
      </div>

      {activeTab === 'overview' ? (
        <div className="stack">
          <div className="admin-summary-grid">
            <article className="panel stat-card"><strong>{preferences.mentorModeEnabled ? t('Enabled') : t('Disabled')}</strong><span className="helper">{t('Mentor Mode')}</span></article>
            <article className="panel stat-card"><strong>{preferences.apprenticeModeEnabled ? t('Enabled') : t('Disabled')}</strong><span className="helper">{t('Apprentice Mode')}</span></article>
            <article className="panel stat-card"><strong>{formatNumber(pendingClaims.length)}</strong><span className="helper">{t('Pending claims')}</span></article>
            <article className="panel stat-card"><strong>{formatNumber(approvedClaims.length)}</strong><span className="helper">{t('Approved skills')}</span></article>
            <article className="panel stat-card"><strong>{formatNumber(activeGoals.length)}</strong><span className="helper">{t('Active goals')}</span></article>
            <article className="panel stat-card"><strong>{formatNumber(activeBookings.length)}</strong><span className="helper">{t('Active bookings')}</span></article>
            <article className="panel stat-card"><strong>{formatNumber(upcomingSessions.length)}</strong><span className="helper">{t('Upcoming sessions')}</span></article>
            <article className="panel stat-card"><strong>{formatNumber(payments.length)}</strong><span className="helper">{t('Payment records')}</span></article>
          </div>
        </div>
      ) : null}

      {activeTab === 'mentor' ? (
        <div className="stack">
          <form className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }} onSubmit={savePreferences}>
            <h3 style={{ margin: 0 }}>{t('Mentor preferences')}</h3>
            <div className="admin-form-grid">
              <label className="checkbox-row"><input type="checkbox" checked={preferences.mentorModeEnabled} onChange={(event) => setPreferences({ ...preferences, mentorModeEnabled: event.target.checked })} /> {t('Mentor Mode enabled')}</label>
              <label className="checkbox-row"><input type="checkbox" checked={preferences.remoteMentorshipEnabled} onChange={(event) => setPreferences({ ...preferences, remoteMentorshipEnabled: event.target.checked })} /> {t('Remote mentorship')}</label>
              <label className="checkbox-row"><input type="checkbox" checked={preferences.onsiteMentorshipEnabled} onChange={(event) => setPreferences({ ...preferences, onsiteMentorshipEnabled: event.target.checked })} /> {t('Onsite mentorship')}</label>
              <label className="admin-form-wide">{t('Bio and preferences')}<textarea className="input" value={preferences.bio} onChange={(event) => setPreferences({ ...preferences, bio: event.target.value })} /></label>
            </div>
            <button className="btn btn-primary" disabled={saving}>{t('Save preferences')}</button>
          </form>

          <form className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }} onSubmit={submitClaim}>
            <h3 style={{ margin: 0 }}>{t('What can you teach?')}</h3>
            <div className="admin-form-grid">
              <div className="admin-form-wide">
                <SkillPicker
                  label={t('Skill')}
                  placeholder={t('Search canonical skills')}
                  selectedSkills={claimSkills}
                  requestContext="mentorship_mentor_claim"
                  disabled={saving}
                  onChange={(nextSkills) => {
                    const next = nextSkills.slice(-1)
                    setClaimSkills(next)
                    setClaimForm({ ...claimForm, skillId: next[0]?.id || '' })
                  }}
                />
              </div>
              <label className="admin-form-wide">{t('Notes')}<input className="input" value={claimForm.notes} onChange={(event) => setClaimForm({ ...claimForm, notes: event.target.value })} /></label>
            </div>
            <button className="btn btn-secondary" disabled={saving || !claimForm.skillId}>{t('Submit claim')}</button>
            {claimableSkills.length === 0 ? <p className="helper">{t('No active shared skills are available to claim.')}</p> : null}
          </form>

          <form className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }} onSubmit={saveRate}>
            <h3 style={{ margin: 0 }}>{t('Approved skill rates')}</h3>
            <div className="admin-form-grid">
              <label>{t('Approved skill')}<select className="input" value={rateForm.skillId} onChange={(event) => setRateForm({ ...rateForm, skillId: event.target.value })}><option value="">{t('Select approved skill')}</option>{approvedClaims.map((claim) => <option key={rowId(claim)} value={skillIdOf(claim)}>{skillNameOf(claim)}</option>)}</select></label>
              <label>{t('Rate')}<input className="input" type="number" min="0.01" step="0.01" value={rateForm.rateAmount} onChange={(event) => setRateForm({ ...rateForm, rateAmount: event.target.value })} /></label>
              <label>{t('Currency')}<input className="input" value={rateForm.currencyCode} onChange={(event) => setRateForm({ ...rateForm, currencyCode: event.target.value.toUpperCase() })} /></label>
              <label>{t('Unit')}<select className="input" value={rateForm.rateUnit} onChange={(event) => setRateForm({ ...rateForm, rateUnit: event.target.value })}><option value="session">{t('Session')}</option><option value="hour">{t('Hour')}</option></select></label>
            </div>
            <button className="btn btn-primary" disabled={saving || !rateForm.skillId || !rateForm.rateAmount}>{t('Save rate')}</button>
            <div className="admin-chip-list">{rates.map((rate) => <span key={rate.id} className="admin-chip">{skillNameOf(rate)} / {formatCurrency(amountOf(rate, 'rateAmount'), rate.currencyCode || 'CAD')} / {titleCase(rate.rateUnit)} / {rate.isActive === false ? t('Inactive') : t('Active')}</span>)}</div>
          </form>

          <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)' }}>
            <h3 style={{ marginTop: 0 }}>{t('Claims')}</h3>
            <div className="admin-chip-list">{claims.map((claim) => <span key={rowId(claim)} className="admin-chip">{skillNameOf(claim)} <span className={statusClass(claim.status)}>{titleCase(claim.status)}</span></span>)}</div>
            {claims.length === 0 ? <EmptyState title={t('No mentor claims yet')} /> : null}
          </div>
          <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)' }}>
            <h3 style={{ marginTop: 0 }}>{t('Shared skills')}</h3>
            <div className="admin-card-grid">{skills.map((skill) => (
              <article key={skillOptionIdOf(skill)} className="panel workspace-panel" style={{ background: 'var(--surface)' }}>
                <h4 style={{ marginTop: 0 }}>{skillLabelOf(skill)}</h4>
                {skill.description ? <p className="helper">{skill.description}</p> : null}
                <div className="admin-chip-list">{renderSkillBadges(skill)}</div>
              </article>
            ))}</div>
            {skills.length === 0 ? <EmptyState title={t('No shared skills found')} description={t('Ask an admin to add skills or add skills to your worker profile.')} /> : null}
          </div>
          <section className="stack"><h3 style={{ margin: 0 }}>{t('Incoming booking requests')}</h3>{renderBookingsTable(incomingBookings, 'mentor')}</section>
        </div>
      ) : null}

      {activeTab === 'apprentice' ? (
        <div className="stack">
          <form className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }} onSubmit={savePreferences}>
            <h3 style={{ margin: 0 }}>{t('Apprentice preferences')}</h3>
            <label className="checkbox-row"><input type="checkbox" checked={preferences.apprenticeModeEnabled} onChange={(event) => setPreferences({ ...preferences, apprenticeModeEnabled: event.target.checked })} /> {t('Apprentice Mode enabled')}</label>
            <button className="btn btn-primary" disabled={saving}>{t('Save preferences')}</button>
          </form>

          <form className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }} onSubmit={createGoal}>
            <h3 style={{ margin: 0 }}>{t('What do you want to learn?')}</h3>
            <div className="admin-form-grid">
              <div className="admin-form-wide">
                <SkillPicker
                  label={t('Skill')}
                  placeholder={t('Search canonical skills')}
                  selectedSkills={goalSkillsSelected}
                  requestContext="mentorship_learning_goal"
                  disabled={saving}
                  onChange={(nextSkills) => {
                    const next = nextSkills.slice(-1)
                    const skillId = next[0]?.id || ''
                    setGoalSkillsSelected(next)
                    setGoalForm({ ...goalForm, skillId })
                    loadSuggestedMentors(skillId)
                  }}
                />
              </div>
              <label>{t('Priority')}<select className="input" value={goalForm.priority} onChange={(event) => setGoalForm({ ...goalForm, priority: event.target.value })}><option value="low">{t('Low')}</option><option value="medium">{t('Medium')}</option><option value="high">{t('High')}</option></select></label>
              <label className="admin-form-wide">{t('Notes')}<input className="input" value={goalForm.notes} onChange={(event) => setGoalForm({ ...goalForm, notes: event.target.value })} /></label>
            </div>
            <button className="btn btn-secondary" disabled={saving || !goalForm.skillId}>{t('Create goal')}</button>
            {goalSkills.length === 0 ? <p className="helper">{t('No active shared skills are available for learning goals.')}</p> : null}
          </form>

          <div className="admin-card-grid">{goals.map((goal) => <article key={goal.id} className="panel workspace-panel" style={{ background: 'var(--surface-muted)' }}><h3 style={{ marginTop: 0 }}>{skillNameOf(goal)}</h3><p className="helper">{goal.notes || t('No notes')}</p><span className={statusClass(goal.status)}>{titleCase(goal.status || 'active')}</span></article>)}</div>

          <form className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }} onSubmit={createBooking}>
            <h3 style={{ margin: 0 }}>{t('Request booking')}</h3>
            <div className="admin-form-grid">
              <label>{t('Skill')}<select className="input" value={bookingForm.skillId} onChange={(event) => loadSuggestedMentors(event.target.value)}><option value="">{t('Select skill')}</option>{activeSkills.map((skill) => <option key={skillOptionIdOf(skill)} value={skillOptionIdOf(skill)}>{skillLabelOf(skill)}</option>)}</select></label>
              <label>{t('Mentor')}<select className="input" value={bookingForm.mentorWorkerProfileId} onChange={(event) => setBookingForm({ ...bookingForm, mentorWorkerProfileId: event.target.value })}><option value="">{t('Select mentor')}</option>{mentorOptions.map((mentor) => <option key={workerIdOf(mentor, 'workerProfileId') || workerIdOf(mentor, 'mentorWorkerProfileId')} value={workerIdOf(mentor, 'workerProfileId') || workerIdOf(mentor, 'mentorWorkerProfileId')}>{mentor.mentorName || mentor.workerName || t('Mentor')} / {formatCurrency(amountOf(mentor, 'rateAmount'), mentor.currencyCode || 'CAD')}</option>)}</select></label>
              <label>{t('Rate')}<select className="input" value={bookingForm.mentorRateId} onChange={(event) => setBookingForm({ ...bookingForm, mentorRateId: event.target.value })}><option value="">{t('Use mentor listed rate')}</option>{rates.map((rate) => <option key={rate.id} value={rate.id}>{skillNameOf(rate)} / {formatCurrency(amountOf(rate, 'rateAmount'), rate.currencyCode || 'CAD')}</option>)}</select></label>
              <label>{t('Start')}<input className="input" type="datetime-local" value={bookingForm.requestedStartAt} onChange={(event) => setBookingForm({ ...bookingForm, requestedStartAt: event.target.value })} /></label>
              <label>{t('End')}<input className="input" type="datetime-local" value={bookingForm.requestedEndAt} onChange={(event) => setBookingForm({ ...bookingForm, requestedEndAt: event.target.value })} /></label>
              <label className="admin-form-wide">{t('Notes')}<input className="input" value={bookingForm.apprenticeNotes} onChange={(event) => setBookingForm({ ...bookingForm, apprenticeNotes: event.target.value })} /></label>
            </div>
            <button className="btn btn-primary" disabled={saving || !bookingForm.skillId || !bookingForm.mentorWorkerProfileId}>{t('Request booking')}</button>
          </form>
          <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)' }}>
            <h3 style={{ marginTop: 0 }}>{t('Shared skills')}</h3>
            <div className="admin-card-grid">{skills.map((skill) => (
              <article key={skillOptionIdOf(skill)} className="panel workspace-panel" style={{ background: 'var(--surface)' }}>
                <h4 style={{ marginTop: 0 }}>{skillLabelOf(skill)}</h4>
                <div className="admin-chip-list">{renderSkillBadges(skill)}</div>
              </article>
            ))}</div>
            {skills.length === 0 ? <EmptyState title={t('No shared skills found')} description={t('Ask an admin to add skills or add skills to your worker profile.')} /> : null}
          </div>
          <section className="stack"><h3 style={{ margin: 0 }}>{t('Active matches')}</h3><div className="admin-chip-list">{matches.map((match) => <span key={match.id} className="admin-chip">{skillNameOf(match)} <span className={statusClass(match.status)}>{titleCase(match.status)}</span></span>)}</div></section>
        </div>
      ) : null}

      {activeTab === 'bookings' ? <div className="stack"><h3>{t('Incoming as mentor')}</h3>{renderBookingsTable(incomingBookings, 'mentor')}<h3>{t('Outgoing as apprentice')}</h3>{renderBookingsTable(outgoingBookings, 'apprentice')}</div> : null}

      {activeTab === 'sessions' ? (
        <div className="stack">
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>{t('When')}</th><th>{t('Mode')}</th><th>{t('Location')}</th><th>{t('Status')}</th><th>{t('Notes')}</th></tr></thead>
              <tbody>{sessions.map((session) => <tr key={session.id}><td>{session.scheduled_start_at || session.scheduledStartAt ? formatDateTime(session.scheduled_start_at || session.scheduledStartAt) : '-'}</td><td>{titleCase(session.delivery_mode || session.deliveryMode)}</td><td>{session.location_id || session.locationId || '-'}</td><td><span className={statusClass(session.status)}>{titleCase(session.status)}</span></td><td>{session.notes || '-'}</td></tr>)}</tbody>
            </table>
          </div>
          <form className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }} onSubmit={submitReview}>
            <h3 style={{ margin: 0 }}>{t('Submit review')}</h3>
            <div className="admin-form-grid">
              <label>{t('Completed match')}<select className="input" value={reviewForm.matchId} onChange={(event) => {
                const match = matches.find((item) => item.id === event.target.value)
                const revieweeWorkerProfileId = workerIdOf(match || {}, 'mentorWorkerProfileId') === myWorkerProfileId ? workerIdOf(match || {}, 'apprenticeWorkerProfileId') : workerIdOf(match || {}, 'mentorWorkerProfileId')
                setReviewForm({ ...reviewForm, matchId: event.target.value, revieweeWorkerProfileId })
              }}><option value="">{t('Select match')}</option>{matches.filter((match) => ['completed', 'active', 'accepted'].includes(String(match.status))).map((match) => <option key={match.id} value={match.id}>{skillNameOf(match)} / {titleCase(match.status)}</option>)}</select></label>
              <label>{t('Rating')}<input className="input" type="number" min={1} max={5} value={reviewForm.rating} onChange={(event) => setReviewForm({ ...reviewForm, rating: event.target.value })} /></label>
              <label>{t('Review type')}<select className="input" value={reviewForm.reviewType} onChange={(event) => setReviewForm({ ...reviewForm, reviewType: event.target.value })}><option value="apprentice_review">{t('Apprentice review')}</option><option value="mentor_review">{t('Mentor review')}</option></select></label>
              <label className="admin-form-wide">{t('Notes')}<input className="input" value={reviewForm.notes} onChange={(event) => setReviewForm({ ...reviewForm, notes: event.target.value })} /></label>
            </div>
            <button className="btn btn-primary" disabled={saving || !reviewForm.matchId}>{t('Submit review')}</button>
          </form>
          <p className="helper">{formatNumber(upcomingSessions.length)} {t('upcoming')} / {formatNumber(completedSessions.length)} {t('completed sessions')}</p>
        </div>
      ) : null}

      {activeTab === 'payments' ? (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead><tr><th>{t('Gross')}</th><th>{t('Platform fee')}</th><th>{t('Mentor net')}</th><th>{t('Status')}</th><th>{t('Method')}</th><th>{t('Notes')}</th></tr></thead>
            <tbody>{payments.map((payment) => <tr key={payment.id}><td>{formatCurrency(amountOf(payment, 'grossAmount'), payment.currencyCode || 'CAD')}</td><td>{formatCurrency(amountOf(payment, 'platformFeeAmount'), payment.currencyCode || 'CAD')}</td><td>{formatCurrency(amountOf(payment, 'mentorNetAmount'), payment.currencyCode || 'CAD')}</td><td><span className={statusClass(payment.paymentStatus || payment.payment_status)}>{titleCase(payment.paymentStatus || payment.payment_status)}</span></td><td>{titleCase(payment.paymentMethod || payment.payment_method)}</td><td>{payment.notes || '-'}</td></tr>)}</tbody>
          </table>
        </div>
      ) : null}
    </section>
  )
}
